<cnt-navbar></cnt-navbar>

<main class="pt-24 md:pt-28 min-h-screen bg-cream bg-grid-subtle">

  @if (!booking) {
    <section class="px-[2%] py-20">
      <div class="max-w-md mx-auto text-center px-4">
        <span class="material-symbols-outlined text-5xl text-muted-text" aria-hidden="true">search_off</span>
        <h1 class="font-headline font-bold text-2xl mt-4 mb-2">Booking not found</h1>
        <p class="text-muted-text text-sm font-body mb-6">We couldn't find that confirmation. Check your trips or browse new stays.</p>
        <div class="flex justify-center gap-3">
          <a routerLink="/user-dashboard/trips" class="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-trinidad text-white text-xs uppercase tracking-[0.12em] font-button font-bold hover:opacity-95">My trips</a>
          <a routerLink="/search" class="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-dark-text/15 text-dark-text text-xs uppercase tracking-[0.12em] font-button font-bold hover:border-trinidad">Browse stays</a>
        </div>
      </div>
    </section>
  } @else {

    <!-- Hero confirmation -->
    <section class="px-[2%] pt-6">
      <div class="max-w-[80rem] mx-auto px-4 md:px-8">

        <!-- Progress steps strip -->
        <ol class="flex items-center gap-3 text-[0.65rem] uppercase tracking-[0.14em] font-button font-bold mb-8 overflow-x-auto">
          <li class="inline-flex items-center gap-2 text-jungle-green">
            <span class="w-6 h-6 rounded-full bg-jungle-green text-white inline-flex items-center justify-center"><span aria-hidden="true" class="material-symbols-outlined text-[14px]">check</span></span>
            Choose dates
          </li>
          <span class="w-8 h-px bg-jungle-green/40"></span>
          <li class="inline-flex items-center gap-2 text-jungle-green">
            <span class="w-6 h-6 rounded-full bg-jungle-green text-white inline-flex items-center justify-center"><span aria-hidden="true" class="material-symbols-outlined text-[14px]">check</span></span>
            Review &amp; confirm
          </li>
          <span class="w-8 h-px bg-jungle-green/40"></span>
          <li class="inline-flex items-center gap-2 text-trinidad">
            <span class="w-6 h-6 rounded-full bg-trinidad text-white inline-flex items-center justify-center"><span aria-hidden="true" class="material-symbols-outlined text-[14px]">check</span></span>
            Confirmed
          </li>
        </ol>

        <!-- Celebration card -->
        <div class="confirm-card confirm-anim relative rounded-3xl overflow-hidden border border-dark-text/8 shadow-[0_24px_48px_rgba(0,0,0,0.06)]"
          style="background: radial-gradient(ellipse at 30% 20%, rgba(251, 215, 132, 0.35) 0%, transparent 50%), radial-gradient(ellipse at 80% 80%, rgba(41, 93, 66, 0.18) 0%, transparent 55%), linear-gradient(135deg, #ffffff 0%, #f7f5ec 100%);">
          <!-- confetti container (filled by GSAP on confirmed bookings) -->
          <!-- fireConfetti() appends 36 spans in here and removes them as each
               tween completes. No ngSkipHydration: the flag is component-host
               only (NG0504), and this route is auth-guarded so it serves as
               RenderMode.Client and never hydrates in the first place. -->
          <div class="confetti-root no-print" aria-hidden="true"></div>
          <!-- decorative sparkle row -->
          <div class="absolute top-6 right-8 hidden md:flex items-center gap-2 text-gold/70 no-print">
            <span aria-hidden="true" class="material-symbols-outlined text-base" style="font-variation-settings: 'FILL' 1;">auto_awesome</span>
            <span aria-hidden="true" class="material-symbols-outlined text-xs" style="font-variation-settings: 'FILL' 1;">auto_awesome</span>
            <span aria-hidden="true" class="material-symbols-outlined text-sm" style="font-variation-settings: 'FILL' 1;">auto_awesome</span>
          </div>
          <div class="px-8 md:px-16 py-10 md:py-14 text-center max-w-2xl mx-auto relative">
            <div class="confirm-checkmark inline-flex items-center justify-center w-16 h-16 rounded-full mb-5 shadow-[0_12px_28px_rgba(41,93,66,0.3)]"
              role="img"
              [attr.aria-label]="'Booking status: ' + booking.status"
              [ngClass]="{
                'bg-jungle-green text-white': booking.status === 'confirmed' || booking.status === 'approved',
                'bg-gold text-dark-text': booking.status === 'pending',
                'bg-trinidad text-white': booking.status === 'declined' || booking.status === 'cancelled'
              }">
              <span class="material-symbols-outlined text-3xl" aria-hidden="true" style="font-variation-settings: 'FILL' 1;">
                @switch (booking.status) {
                  @case ('pending') { hourglass_top }
                  @case ('declined') { do_not_disturb_on }
                  @case ('cancelled') { cancel }
                  @default { check_circle }
                }
              </span>
            </div>
            <span class="font-label uppercase tracking-[0.14em] text-[0.7rem] font-bold block mb-2"
              role="status" aria-live="polite"
              [ngClass]="{
                'text-jungle-green': booking.status === 'approved',
                'text-trinidad': booking.status !== 'approved'
              }">
              @switch (booking.status) {
                @case ('approved') { Host approved }
                @case ('declined') { Host declined }
                @case ('pending') { {{ booking.changeRequested ? 'Change requested' : (isHostViewer ? 'New request' : 'Awaiting host') }} }
                @case ('cancelled') { Cancelled }
                @case ('confirmed') { {{ booking.instantBook ? 'Instant booking' : 'Confirmed' }} }
              }
            </span>
            <h1 class="font-headline font-bold text-dark-text tracking-tight leading-[1.05] text-4xl md:text-5xl mb-2">
              @switch (booking.status) {
                @case ('confirmed') { You're booked! }
                @case ('approved') { You're booked! }
                @case ('pending') {
                  @if (isHostViewer) { {{ booking.changeRequested ? 'Your guest wants to change dates' : 'Your guest wants to book' }} }
                  @else if (booking.changeRequested) { Change waiting on the host }
                  @else { Waiting on the host }
                }
                @case ('declined') { Couldn't host this trip }
                @case ('cancelled') { Trip cancelled }
              }
            </h1>
            <div class="font-headline font-bold text-dark-text text-lg md:text-xl mb-1">{{ booking.listingTitle }}</div>
            <div class="inline-flex items-center gap-1.5 text-muted-text text-sm font-body mb-5">
              <span aria-hidden="true" class="material-symbols-outlined text-base text-jungle-green" style="font-variation-settings: 'FILL' 1;">location_on</span>
              <span>{{ booking.hostAddress || booking.listingLocation }}</span>
            </div>
            <p class="text-muted-text text-base md:text-lg font-body leading-relaxed mb-4 max-w-xl mx-auto">
              @switch (booking.status) {
                @case ('confirmed') { Your reservation is locked in. Plan to arrive between 2 PM and 8 PM on day one. }
                @case ('approved') { The host accepted your request. Your reservation is locked in — plan to arrive between 2 PM and 8 PM on day one. }
                @case ('pending') {
                  @if (booking.changeRequested) {
                    {{ isHostViewer ? 'Approve to move the booking to the new dates, or decline to keep it as booked.' : 'Your current booking stands until the host approves the change.' }}
                  } @else if (isHostViewer) {
                    Approve to lock in the stay, or decline to release the dates.
                  } @else {
                    We've sent your request to the host. They typically respond within an hour.
                  }
                }
                @case ('declined') { Your host couldn't fit this stay into their calendar. Try one of our similar stays nearby. }
                @case ('cancelled') { This booking has been cancelled. A refund (if applicable) will appear within 5 business days. }
              }
            </p>

            @if (booking.changeRequested && booking.pendingChange) {
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6 text-left max-w-lg mx-auto">
                <div class="rounded-xl bg-white/80 border border-dark-text/8 p-4">
                  <div class="text-[0.65rem] font-label uppercase tracking-[0.12em] text-muted-text font-bold">Booked</div>
                  <div class="text-sm font-body font-bold text-dark-text mt-1">{{ datesLabel }}</div>
                  <div class="text-xs text-muted-text font-body">{{ booking.total | currency: 'USD' : 'symbol' : '1.2-2' }}</div>
                </div>
                <div class="rounded-xl bg-gold/15 border border-gold/40 p-4">
                  <div class="text-[0.65rem] font-label uppercase tracking-[0.12em] text-[#8a5a00] font-bold">Requested</div>
                  <div class="text-sm font-body font-bold text-dark-text mt-1">{{ pendingDatesLabel }}</div>
                  <div class="text-xs text-muted-text font-body">{{ booking.pendingChange.total | currency: 'USD' : 'symbol' : '1.2-2' }}</div>
                </div>
              </div>
            }

            @if (awaitingHostDecision) {
              <div class="no-print flex flex-wrap items-center justify-center gap-2 mb-6">
                <button type="button" (click)="decide('approved')" [disabled]="deciding"
                  class="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-jungle-green text-white text-xs uppercase tracking-[0.12em] font-button font-bold hover:opacity-95 disabled:opacity-50">
                  <span aria-hidden="true" class="material-symbols-outlined text-base">check</span>
                  {{ booking.changeRequested ? 'Approve change' : 'Approve' }}
                </button>
                <button type="button" (click)="declineOpen = !declineOpen" [disabled]="deciding"
                  class="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-dark-text/15 text-dark-text text-xs uppercase tracking-[0.12em] font-button font-bold hover:border-trinidad hover:text-trinidad disabled:opacity-50">
                  <span aria-hidden="true" class="material-symbols-outlined text-base">close</span>
                  {{ booking.changeRequested ? 'Decline change' : 'Decline' }}
                </button>
              </div>
              @if (declineOpen) {
                <div class="no-print max-w-md mx-auto mb-6 text-left">
                  <label class="flex flex-col gap-2">
                    <span class="text-xs font-label uppercase tracking-[0.1em] text-muted-text font-bold">Reason <span class="text-muted-text/70 normal-case font-normal">(shared with your guest)</span></span>
                    <textarea [(ngModel)]="declineReason" name="declineReason" rows="2" maxlength="240"
                      class="bg-white border border-dark-text/15 rounded-md px-4 py-3 text-sm font-body text-dark-text focus:outline-none focus:border-jungle-green focus:ring-2 focus:ring-jungle-green/15 resize-none"></textarea>
                  </label>
                  <p class="text-xs text-muted-text font-body mt-2">
                    {{ booking.changeRequested ? 'The original booking stays as it is.' : 'The dates are released and your guest is notified.' }}
                  </p>
                  <div class="flex justify-end mt-3">
                    <button type="button" (click)="decide('declined')" [disabled]="deciding"
                      class="px-5 py-2.5 rounded-full bg-trinidad text-white text-xs uppercase tracking-[0.12em] font-button font-bold hover:opacity-95 disabled:opacity-50">
                      {{ booking.changeRequested ? 'Decline change' : 'Decline request' }}
                    </button>
                  </div>
                </div>
              }
            } @else if (booking.status === 'pending') {
              <div class="flex flex-wrap items-center justify-center gap-2 mb-6">
                <span class="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gold/15 border border-gold/40 text-dark-text text-[0.7rem] font-button uppercase tracking-[0.12em] font-bold">
                  <span aria-hidden="true" class="material-symbols-outlined text-[14px]" style="font-variation-settings: 'FILL' 1;">schedule</span>
                  {{ booking.changeRequested ? 'Change awaiting host' : 'Awaiting host response' }}
                </span>
                @if (isCancellable && !booking.changeRequested) {
                  <button type="button" (click)="openCancelModal()"
                    class="no-print inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-dark-text/15 text-dark-text text-[0.7rem] font-button uppercase tracking-[0.12em] font-bold hover:border-trinidad hover:text-trinidad transition-colors">
                    <span aria-hidden="true" class="material-symbols-outlined text-[14px]">close</span>
                    Cancel request
                  </button>
                }
              </div>
              <p class="text-[0.75rem] text-muted-text font-body italic mb-6 max-w-xl mx-auto">
                Hosts have up to 24 hours to respond. We'll email + notify in-app the moment they do — no need to refresh.
              </p>
            }
            @if (booking.status === 'declined') {
              <a routerLink="/search" class="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-trinidad text-white text-xs uppercase tracking-[0.12em] font-button font-bold hover:opacity-95 shadow-[0_10px_24px_rgba(227,83,13,0.25)] mb-2">
                Browse similar stays
                <span aria-hidden="true" class="material-symbols-outlined text-base">arrow_forward</span>
              </a>
            }

            <!-- Status pill row: Verified guest + Confirmation # -->
            <div class="flex flex-wrap items-center justify-center gap-2">
              @if (guestVerified && (booking.status === 'confirmed' || booking.status === 'approved')) {
                <span class="inline-flex items-center gap-1.5 pl-2.5 pr-3 py-1.5 rounded-full bg-jungle-green/10 text-jungle-green text-[0.7rem] font-button uppercase tracking-[0.12em] font-bold">
                  <span aria-hidden="true" class="material-symbols-outlined text-[14px]" style="font-variation-settings: 'FILL' 1;">verified</span>
                  Verified guest
                </span>
              }
              <span class="inline-flex items-center gap-2 pl-3 pr-3 py-1.5 rounded-full bg-white border border-dark-text/8 shadow-sm">
                <span class="text-[0.65rem] font-label uppercase tracking-[0.12em] text-muted-text font-bold">Confirmation</span>
                <span class="font-headline font-bold text-dark-text text-sm tracking-wider">#{{ confirmationNumber }}</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="px-[2%] py-12">
      <div class="max-w-[80rem] mx-auto px-4 md:px-8">
        <!-- Below lg the page is one column. The left column's cards join the grid
             (display: contents) so the actions and price card can sit right after
             the trip card instead of below every other card. -->
        <div class="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10">

          <!-- Left: details -->
          <div class="contents lg:block lg:col-span-7 lg:space-y-6">

            <!-- Trip card -->
            <div class="confirm-anim max-lg:order-1 bg-white rounded-2xl border border-dark-text/8 shadow-[0_4px_16px_rgba(0,0,0,0.03)] overflow-hidden">
              <div class="relative">
                <img [src]="booking.listingPhoto" [alt]="booking.listingTitle" class="w-full h-56 md:h-72 object-cover">
                @if (guestVerified && booking.status !== 'cancelled' && booking.status !== 'declined') {
                  <div class="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/95 backdrop-blur-sm shadow-md ring-1 ring-jungle-green/20">
                    <span aria-hidden="true" class="material-symbols-outlined text-base text-jungle-green" style="font-variation-settings: 'FILL' 1;">verified</span>
                    <span class="text-[0.65rem] font-button uppercase tracking-[0.1em] font-bold text-jungle-green">Verified guest</span>
                  </div>
                }
              </div>
              <div class="p-6 md:p-8">
                <span class="text-trinidad font-label uppercase tracking-[0.14em] text-[0.7rem] font-bold block mb-2">Your stay</span>
                <h2 class="font-headline font-bold text-dark-text text-2xl md:text-3xl leading-tight tracking-tight mb-2">{{ booking.listingTitle }}</h2>
                <p class="text-muted-text text-sm font-body mb-6">{{ booking.listingLocation }}</p>
                <div class="grid grid-cols-2 sm:grid-cols-3 gap-5 pt-5 border-t border-dark-text/8">
                  <div>
                    <div class="text-[0.65rem] font-label uppercase tracking-[0.1em] text-muted-text font-bold">Check-in</div>
                    <div class="text-sm font-body font-bold text-dark-text mt-1">{{ datesLabel.split(' – ')[0] }}</div>
                    <div class="text-xs text-muted-text font-body">2 – 8 PM</div>
                  </div>
                  <div>
                    <div class="text-[0.65rem] font-label uppercase tracking-[0.1em] text-muted-text font-bold">Check-out</div>
                    <div class="text-sm font-body font-bold text-dark-text mt-1">{{ datesLabel.split(' – ')[1] }}</div>
                    <div class="text-xs text-muted-text font-body">By 11 AM</div>
                  </div>
                  <div class="col-span-2 sm:col-span-1">
                    <div class="text-[0.65rem] font-label uppercase tracking-[0.1em] text-muted-text font-bold">Trip</div>
                    <div class="text-sm font-body font-bold text-dark-text mt-1">{{ booking.nights }} {{ booking.nights === 1 ? 'night' : 'nights' }} · {{ booking.guests }} {{ booking.guests === 1 ? 'guest' : 'guests' }}</div>
                    <div class="text-xs text-muted-text font-body">Rig: {{ booking.rvSummary }}</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Host card -->
            <div class="confirm-anim max-lg:order-3 bg-white rounded-2xl border border-dark-text/8 shadow-[0_4px_16px_rgba(0,0,0,0.03)] p-6 md:p-8 flex items-center gap-5">
              <div class="w-16 h-16 rounded-full bg-jungle-green text-white flex items-center justify-center font-headline font-bold text-xl shrink-0">
                {{ hostInitials }}
              </div>
              <div class="flex-1 min-w-0">
                <span class="text-trinidad font-label uppercase tracking-[0.14em] text-[0.7rem] font-bold block">{{ isHostViewer ? 'Host' : 'Your host' }}</span>
                <h3 class="font-headline font-bold text-dark-text text-lg leading-tight">{{ booking.hostName }}</h3>
                <p class="text-xs text-muted-text font-body mt-0.5">Typically replies within 24 hours</p>
              </div>
              <a [routerLink]="'/contact'" [queryParams]="{ reason: 'guest-support', listingId: booking.listingId }"
                class="hidden sm:inline-flex shrink-0 items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-dark-text/15 text-dark-text text-xs uppercase tracking-[0.12em] font-button font-bold hover:border-trinidad transition-colors">
                <span aria-hidden="true" class="material-symbols-outlined text-base">forum</span>
                Message
              </a>
              <a [routerLink]="'/contact'" [queryParams]="{ reason: 'guest-support', listingId: booking.listingId }"
                aria-label="Message"
                class="sm:hidden shrink-0 inline-flex items-center justify-center w-11 h-11 rounded-full bg-white border border-dark-text/15 text-dark-text hover:border-trinidad transition-colors">
                <span aria-hidden="true" class="material-symbols-outlined text-xl">forum</span>
              </a>
            </div>

            <!-- Address reveal (when confirmed or host-approved) -->
            @if (booking.status === 'confirmed' || booking.status === 'approved') {
              <div class="confirm-anim max-lg:order-3 bg-white rounded-2xl border border-dark-text/8 shadow-[0_4px_16px_rgba(0,0,0,0.03)] p-6 md:p-8 transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_16px_40px_rgba(0,0,0,0.08)]">
                <div class="flex items-center gap-2 mb-1">
                  <span aria-hidden="true" class="material-symbols-outlined text-trinidad" style="font-variation-settings: 'FILL' 1;">location_on</span>
                  <span class="text-trinidad font-label uppercase tracking-[0.14em] text-[0.7rem] font-bold">Approximate location</span>
                </div>
                <h3 class="font-headline font-bold text-dark-text text-xl md:text-2xl tracking-tight leading-tight mb-4">{{ booking.listingLocation }}</h3>
                @if (booking.lat != null && booking.lng != null) {
                  <cnt-mini-map [lat]="booking.lat" [lng]="booking.lng" [label]="booking.listingTitle" class="block mb-4"></cnt-mini-map>
                }
                <div class="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
                  <p class="text-muted-text text-sm font-body flex-1">Exact location shared 24 hrs before check-in.</p>
                  @if (booking.lat != null && booking.lng != null) {
                    <a [href]="'https://maps.google.com/?q=' + booking.lat + ',' + booking.lng" target="_blank" rel="noopener"
                      class="shrink-0 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-white border border-dark-text/15 text-dark-text text-xs uppercase tracking-[0.12em] font-button font-bold hover:border-trinidad hover:text-trinidad transition-colors no-underline">
                      <span aria-hidden="true" class="material-symbols-outlined text-base">directions</span>
                      Open in Maps
                    </a>
                  }
                </div>
              </div>
            }

            <!-- What's next timeline -->
            <div class="confirm-anim max-lg:order-3 bg-white rounded-2xl border border-dark-text/8 shadow-[0_4px_16px_rgba(0,0,0,0.03)] p-6 md:p-8">
              <span class="text-trinidad font-label uppercase tracking-[0.14em] text-[0.7rem] font-bold block mb-1">What's next</span>
              <h3 class="font-headline font-bold text-dark-text text-2xl leading-tight tracking-tight mb-6">Your trip in 3 steps</h3>
              <ol class="relative space-y-6 pl-2">
                <span class="absolute left-[14px] top-3 bottom-3 w-px bg-jungle-green/20" aria-hidden="true"></span>
                <li class="flex gap-4 relative">
                  <span class="relative z-10 shrink-0 w-7 h-7 rounded-full bg-jungle-green text-white font-headline font-bold text-sm flex items-center justify-center ring-4 ring-cream">1</span>
                  <div>
                    <div class="font-body font-bold text-dark-text">Your host will reach out</div>
                    <div class="text-sm text-muted-text font-body mt-1">Expect a welcome message with arrival details within 24 hours.</div>
                  </div>
                </li>
                <li class="flex gap-4 relative">
                  <span class="relative z-10 shrink-0 w-7 h-7 rounded-full bg-jungle-green text-white font-headline font-bold text-sm flex items-center justify-center ring-4 ring-cream">2</span>
                  <div>
                    <div class="font-body font-bold text-dark-text">Plan your arrival</div>
                    <div class="text-sm text-muted-text font-body mt-1">Add the dates to your calendar. We'll resend address details on day-of.</div>
                  </div>
                </li>
                <li class="flex gap-4 relative">
                  <span class="relative z-10 shrink-0 w-7 h-7 rounded-full bg-trinidad text-white font-headline font-bold text-sm flex items-center justify-center ring-4 ring-cream">3</span>
                  <div>
                    <div class="font-body font-bold text-dark-text">Earn $5/night Review Reward</div>
                    <div class="text-sm text-muted-text font-body mt-1">Leave a 5-star review after your stay to claim the credit on your next trip.</div>
                  </div>
                </li>
              </ol>
            </div>

          </div>

          <!-- Right: actions + price + trust -->
          <aside class="max-lg:order-2 lg:col-span-5">
            <div class="lg:sticky lg:top-32 space-y-5">

              <div class="confirm-anim bg-white rounded-2xl border border-dark-text/8 shadow-[0_8px_24px_rgba(0,0,0,0.04)] p-6">
                <span class="text-trinidad font-label uppercase tracking-[0.14em] text-[0.7rem] font-bold block mb-4">Total</span>
                <div class="flex flex-col gap-3 text-sm font-body text-dark-text">
                  <div class="flex justify-between">
                    <span>{{ booking.pricePerNight | currency: 'USD' : 'symbol' : '1.2-2' }} × {{ booking.nights }} {{ booking.nights === 1 ? 'night' : 'nights' }}</span>
                    <span>{{ booking.subtotal | currency: 'USD' : 'symbol' : '1.2-2' }}</span>
                  </div>
                  @if (booking.addOns?.length) {
                    @for (a of booking.addOns; track a.id) {
                      <div class="flex justify-between text-muted-text">
                        <span class="inline-flex items-center gap-1.5 truncate pr-2">
                          @if (a.photo) {
                            <img [src]="a.photo" alt="" class="shrink-0 w-5 h-5 rounded object-cover">
                          } @else if (a.icon) {
                            <span aria-hidden="true" class="material-symbols-outlined text-[16px] shrink-0 text-jungle-green">{{ a.icon }}</span>
                          } @else {
                            <span class="shrink-0">+</span>
                          }
                          <span class="truncate">{{ a.label }}</span>
                          @if (a.quantity > 1) {
                            <span class="text-[0.65rem]">× {{ a.quantity }}</span>
                          }
                        </span>
                        <span>{{ a.amount | currency: 'USD' : 'symbol' : '1.2-2' }}</span>
                      </div>
                    }
                  }
                  <!-- "Included" line — feedback incentive is baked into the nightly rate (all-in pricing). -->
                  @if (booking.feedbackIncentive && booking.feedbackIncentive > 0) {
                    <div class="text-[0.7rem] text-muted-text -mt-1 pl-4">
                      Includes {{ booking.feedbackIncentive | currency: 'USD' : 'symbol' : '1.0-2' }} feedback incentive · refunds as CurbNTurf Cash after your review
                    </div>
                  }
                  @for (d of booking.discounts ?? []; track d.label) {
                    <div class="flex justify-between text-jungle-green">
                      <span>{{ d.label }}</span>
                      <span>−{{ d.amount | currency: 'USD' : 'symbol' : '1.2-2' }}</span>
                    </div>
                  }
                  @if (booking.taxTotal && booking.taxTotal > 0) {
                    <div class="flex justify-between text-muted-text">
                      <span>Taxes</span>
                      <span>{{ booking.taxTotal | currency: 'USD' : 'symbol' : '1.2-2' }}</span>
                    </div>
                  }
                </div>
                <hr class="my-4 border-dark-text/8">
                <div class="flex justify-between items-baseline">
                  <span class="font-headline font-bold text-dark-text">Total</span>
                  <span class="font-headline font-bold text-trinidad text-3xl">{{ booking.total | currency: 'USD' : 'symbol' : '1.2-2' }}</span>
                </div>
                <!-- CurbNTurf Cash is a separate payment against the total, not a price line. -->
                @if (booking.creditApplied && booking.creditApplied > 0) {
                  <div class="flex flex-col gap-2 mt-3 pt-3 border-t border-dark-text/8 text-sm font-body">
                    <div class="flex justify-between text-trinidad">
                      <span class="inline-flex items-center gap-1.5">
                        <span aria-hidden="true" class="material-symbols-outlined text-[14px]" style="font-variation-settings: 'FILL' 1;">redeem</span>
                        CurbNTurf Cash applied
                      </span>
                      <span>−{{ booking.creditApplied | currency: 'USD' : 'symbol' : '1.2-2' }}</span>
                    </div>
                    <div class="flex justify-between font-bold text-dark-text">
                      <span>{{ booking.status === 'pending' && !booking.changeRequested ? 'Card charge on approval' : 'Paid by card' }}</span>
                      <span>{{ paidByCard | currency: 'USD' : 'symbol' : '1.2-2' }}</span>
                    </div>
                  </div>
                }
              </div>

              <a routerLink="/user-dashboard/trips" class="no-print w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-trinidad text-white font-button uppercase tracking-[0.12em] text-sm font-bold hover:opacity-95 transition-opacity shadow-[0_10px_24px_rgba(227,83,13,0.25)]">
                View all trips
                <span aria-hidden="true" class="material-symbols-outlined text-base">arrow_forward</span>
              </a>

              <!-- Action bar -->
              <div class="no-print bg-white rounded-2xl border border-dark-text/8 shadow-[0_4px_16px_rgba(0,0,0,0.03)] divide-y divide-dark-text/8 overflow-hidden">
                <button type="button" (click)="downloadIcs()"
                  class="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-cream/60 transition-colors">
                  <span aria-hidden="true" class="material-symbols-outlined text-jungle-green">calendar_add_on</span>
                  <span class="flex-1 text-sm font-body font-bold text-dark-text">Add to calendar</span>
                  <span class="text-[0.65rem] font-button uppercase tracking-[0.1em] font-bold text-muted-text">.ICS</span>
                </button>
                <button type="button" (click)="shareTrip()"
                  class="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-cream/60 transition-colors">
                  <span aria-hidden="true" class="material-symbols-outlined text-jungle-green">share</span>
                  <span class="flex-1 text-sm font-body font-bold text-dark-text">Share trip</span>
                  <span aria-hidden="true" class="material-symbols-outlined text-base text-muted-text">arrow_forward_ios</span>
                </button>
                <button type="button" (click)="printReceipt()"
                  class="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-cream/60 transition-colors">
                  <span aria-hidden="true" class="material-symbols-outlined text-jungle-green">print</span>
                  <span class="flex-1 text-sm font-body font-bold text-dark-text">Print receipt</span>
                  <span aria-hidden="true" class="material-symbols-outlined text-base text-muted-text">arrow_forward_ios</span>
                </button>
                @if (isModifiable) {
                  <button type="button" (click)="openModifyModal()"
                    class="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-cream/60 transition-colors">
                    <span aria-hidden="true" class="material-symbols-outlined text-jungle-green">edit_calendar</span>
                    <span class="flex-1 text-sm font-body font-bold text-dark-text">{{ booking.realReservationId ? 'Modify dates or add-ons' : 'Modify dates or guests' }}</span>
                    <span aria-hidden="true" class="material-symbols-outlined text-base text-muted-text">arrow_forward_ios</span>
                  </button>
                } @else if (modifyBlockedReason) {
                  <div class="w-full flex items-center gap-3 px-5 py-4 text-left">
                    <span aria-hidden="true" class="material-symbols-outlined text-muted-text/60">edit_calendar</span>
                    <span class="flex-1">
                      <span class="block text-sm font-body font-bold text-muted-text">Modify dates or add-ons</span>
                      <span class="block text-xs font-body text-muted-text">{{ modifyBlockedReason }}</span>
                    </span>
                  </div>
                }
                @if (isCancellable) {
                  <button type="button" (click)="openCancelModal()"
                    class="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-trinidad/5 transition-colors">
                    <span aria-hidden="true" class="material-symbols-outlined text-trinidad">cancel</span>
                    <span class="flex-1 text-sm font-body font-bold text-trinidad">{{ booking.status === 'pending' && !booking.changeRequested ? 'Cancel request' : 'Cancel booking' }}</span>
                  </button>
                }
              </div>

              <!-- Trust strip -->
              <div class="confirm-anim bg-jungle-green/5 rounded-2xl border border-jungle-green/15 p-5 space-y-3">
                <!-- <div class="flex items-start gap-3">
                  <span aria-hidden="true" class="material-symbols-outlined text-jungle-green shrink-0 mt-0.5" style="font-variation-settings: 'FILL' 1;">verified_user</span>
                  <div>
                    <div class="text-sm font-body font-bold text-dark-text">Verified host</div>
                    <div class="text-xs text-muted-text font-body">Identity, ownership, and insurance checked.</div>
                  </div>
                </div> -->
                <div class="flex items-start gap-3">
                  <span aria-hidden="true" class="material-symbols-outlined text-jungle-green shrink-0 mt-0.5" style="font-variation-settings: 'FILL' 1;">policy</span>
                  <div>
                    <div class="text-sm font-body font-bold text-dark-text">{{ policyLabel }} cancellation</div>
                    <div class="text-xs text-muted-text font-body">{{ policyText }}</div>
                  </div>
                </div>
                <div class="flex items-start gap-3">
                  <span aria-hidden="true" class="material-symbols-outlined text-jungle-green shrink-0 mt-0.5" style="font-variation-settings: 'FILL' 1;">support_agent</span>
                  <div>
                    <div class="text-sm font-body font-bold text-dark-text">24/7 trip support</div>
                    <div class="text-xs text-muted-text font-body">Reach a real person any time.</div>
                  </div>
                </div>
              </div>

              <a [routerLink]="'/contact'" [queryParams]="{ reason: 'guest-support', listingId: booking.listingId }"
                class="block text-center text-xs text-muted-text font-body hover:text-trinidad mt-2">
                Need help? Message support
              </a>

            </div>
          </aside>

        </div>
      </div>
    </section>

    @if (cancelOpen) {
      <cnt-booking-cancel-modal [booking]="booking" [asHost]="isHostViewer"
        (closed)="cancelOpen = false" (cancelled)="onCancelled($event)"></cnt-booking-cancel-modal>
    }

    @if (modifyOpen) {
      <cnt-booking-modify-modal [booking]="booking"
        (closed)="modifyOpen = false" (saved)="onModified($event)"></cnt-booking-modify-modal>
    }

  }
</main>

<curbnturf-footer></curbnturf-footer>
